import React, { useCallback, useEffect, useMemo } from 'react'
import SignInModal from './SignIn'
import { Channel, NonExternalCoupon } from 'shared-types/Coupon'
import { OfferType } from 'shared-types/Coupon'
import { inject, observer } from 'mobx-react'
import BaseModal from './Base'
import CouponButton, { ButtonType, Origin } from '../CouponButton'
import { ActionType, toButtonType } from './utils'
import styled from 'styled-components'
import CopyCode from '../CopyCode'
import type { LanguageLocale } from 'utils/language'
import CouponsStore from 'mobx/Coupons'
import Availability from './Availability'
import { SnackbarStatus } from 'mobx/Infra/Infra.type'
import type _Infra from 'mobx/Infra'
import type _Application from 'mobx/Application'
import type _Account from 'mobx/Account'

import type CouponError from 'mobx/Coupons/errors'

import QRCodeCanvas from 'components/common/QRCode'
import { QRCodeType } from 'utils/constants'
import { Qr as QrSkeleton } from '../CouponCodeSkeleton'

import useAuthenticator from 'hooks/useAuthenticator'

interface Props {
	coupon: NonExternalCoupon
	locale: LanguageLocale
	applied: boolean
	signIn: boolean
	save: boolean
	disabled?: boolean
	error?: string
	onClose?: () => void
}

const CodeAndTip = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
`

const Section = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`

const Line = styled.div`
	width: 100%;
	min-height: 2px;

	background-image: linear-gradient(to right, var(--disable) 50%, var(--clear) 0%);
	background-position: top;
	background-size: 0.75em 1px;
	background-repeat: repeat;
`

const KioskQrCode = inject('Application', 'Account')(
	observer(({coupon, Application, Account}: {coupon: NonExternalCoupon, Application: typeof _Application, Account: typeof _Account}) => {
		const { 
			start: startAuthenticator, 
			connectionState: authenticatorState, 
			isConnected: authenticatorIsConnected 
		} = useAuthenticator(Application, Account, () => {
			startAuthenticator()
		})

		useEffect(() => {
			if(requireLogin) {
				startAuthenticator()
			}
		}, [])

		const requireLogin = !!coupon.flags.requireLogin?.value || Account.isAuthenticated()

		return (
			<>
			{(!requireLogin || authenticatorIsConnected) &&
				<QRCodeCanvas content={{type: QRCodeType.Coupon, data: {couponCode: coupon.code}}} />
			}
			{requireLogin && !authenticatorIsConnected && (
				authenticatorState.code ? (
					<QRCodeCanvas content={{type: QRCodeType.RequireLoginCoupon, data: {couponCode: coupon.code, authCode: authenticatorState.code}}} />
				) : (
					<QrSkeleton />
				)
			)}
			</>
		)
	})
)

const InternalModal: React.FC<Props & { Infra: typeof _Infra }> = ({
	coupon,
	applied,
	signIn,
	save,
	disabled,
	error: parentError,
	locale,
	onClose,
	Infra
}) => {

	const kioskCoupon = coupon.channels.includes(Channel.KIOSK)
	const actionType = coupon.offerType === OfferType.ITEM ? ActionType.AddToCart : ActionType.Apply
	const secondActionType = coupon.offerType !== OfferType.ITEM && kioskCoupon ? ActionType.View : undefined

	const localizedCoupon = useMemo(() => CouponsStore.localizeCoupon(coupon, locale), [coupon, locale])
	const handleError = useCallback(
		(error: CouponError, actions: Record<ButtonType, () => Promise<void>>, disableSnackbar?: boolean) => {
			if (error.continueFlow && !disableSnackbar) {
				Infra?.showSnackbar({
					status: SnackbarStatus.INFO,
					message: error.text(),
					key: 'coupons-error-snackbar',
					snackId: 'coupons-error-snackbar',
					isAttachedToElement: false,
				})

				actions[ButtonType.APPLY]()
			}
		},
		[Infra]
	)

	return signIn ? (
		<SignInModal
			coupon={coupon}
			error={parentError}
			locale={locale}
			save={save}
			disabled={disabled}
			actionType={actionType}
			secondActionType={secondActionType}
			info={<Availability coupon={coupon} locale={locale} />}
			onError={handleError}
			onClose={onClose}
		/>
	) : (
		<BaseModal
			coupon={localizedCoupon}
			button={
				<CouponButton
					coupon={coupon}
					variant={toButtonType(actionType, { save, applied, signIn: false })}
					disabled={disabled}
					onError={handleError}
					origin={Origin.MODAL}
				/>
			}
			onClose={onClose}
			error={parentError}
			content={
				coupon.offerType !== OfferType.ITEM ? (
					<>
						<Line />
						<CodeAndTip>
							<Section>
								<CopyCode code={coupon.code!} />
							</Section>
						</CodeAndTip>
						{kioskCoupon &&
							<KioskQrCode coupon={coupon} />
						}
					</>
				) : undefined
			}
			info={<Availability coupon={coupon} locale={locale} />}
		/>
	)
}

export default inject('Infra')(observer(InternalModal)) as React.FC<Props>
